import { motion } from "motion/react";
import { ArrowRight, type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

interface ButtonProps {
  children: ReactNode;
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  icon?: LucideIcon;
  showArrow?: boolean;
  onClick?: () => void;
  className?: string;
}

export default function Button({
  children,
  variant = "primary",
  size = "md",
  icon: Icon,
  showArrow = false,
  onClick,
  className = "",
}: ButtonProps) {
  const baseStyles =
    "font-semibold rounded-lg transition-all inline-flex items-center gap-2 md:gap-4";

  const variants = {
    primary:
      "bg-gradient-to-r from-primary-400 to-primary-500 text-white hover:shadow-xl hover:shadow-primary-500/30",
    secondary:
      "bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20",
    outline:
      "bg-white border-2 border-primary-500 text-primary-600 hover:bg-primary-50",
  };

  const sizes = {
    sm: "px-3 py-1 text-sm",
    md: "px-5 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
      className={`group ${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {Icon && <Icon className="w-5 h-5" />}
      <span>{children}</span>
      {showArrow && (
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      )}
    </motion.button>
  );
}
